import { useContext, useMemo } from 'react'

import { OptimizelyContext } from '@optimizely/react-sdk'
import { shallowEqual, useSelector } from 'react-redux'

import { useGoustoAnytimeMenuVariant } from 'routes/Menu/experiments/GoustoAnytime/useGoustoAnytimeMenuVariant'
import { getOrderDiscount } from 'selectors/basket'
import { formatPrice } from 'utils/format'

const DISCOUNT_VISIBILITY_FLAG = 'tomatoes_ofx_discount_visibility_confirmation'

export type DiscountSavingsBannerData = {
  shouldShow: boolean
  percentage: string
  amountSaved: string
}

export const useShowDiscountSavingsBanner = (
  isTransactional: boolean,
  showHeader: boolean,
): DiscountSavingsBannerData => {
  const { optimizely } = useContext(OptimizelyContext)
  const { discountPercent, amountSaved, discountApplied } = useSelector(
    getOrderDiscount,
    shallowEqual,
  )
  const isInGoustoAnytimeVariant = useGoustoAnytimeMenuVariant()
  // showHeader is false for market-only visits (e.g. My Gousto links with no
  // order_action), where the banner's section never renders — exclude them.
  const isEligibleForBanner =
    showHeader && isInGoustoAnytimeVariant && isTransactional && discountApplied

  // Decide only when eligible so the OFX_DECISION exposure isn't emitted for
  // ineligible renders. Memoised so re-renders don't re-emit.
  const enabled = useMemo(
    () =>
      optimizely && isEligibleForBanner
        ? optimizely.decide(DISCOUNT_VISIBILITY_FLAG).enabled
        : false,
    [optimizely, isEligibleForBanner],
  )

  return {
    shouldShow: enabled,
    percentage: `${Math.ceil(discountPercent)}%`,
    amountSaved: formatPrice(amountSaved),
  }
}
